import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { webSearchConfigSchema, type AgentInput } from '@24talk/shared';
import { toAgentInput } from '../agent-form';
import { Card, Page, State } from '../components';
import { AgentSharing } from '../AgentSharing';

interface Origin {
  id: string;
  origin: string;
}
type AgentForm = AgentInput;
interface Agent extends AgentForm {
  id: string;
  publicId: string;
  allowedOrigins: Origin[];
}
const blank: AgentForm = {
  displayName: '',
  greeting: 'Hi! How can I help?',
  instructions: '',
  primaryColor: '#2563eb',
  isActive: false,
  contactCaptureEnabled: true,
  humanHandoffEnabled: true,
};

export function AgentEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['agent', id],
    queryFn: () => api<Agent>(`/api/v1/agents/${id}`),
    enabled: !isNew,
  });
  const [form, setForm] = useState<AgentForm>(blank);
  const [origin, setOrigin] = useState('');
  const [searchDomains, setSearchDomains] = useState('');
  const [searchError, setSearchError] = useState('');
  useEffect(() => {
    if (query.data) {
      setForm(toAgentInput(query.data));
      setSearchDomains((query.data.allowedSearchDomains ?? []).join('\n'));
    }
  }, [query.data]);
  const save = useMutation({
    mutationFn: () =>
      api<Agent>(isNew ? '/api/v1/agents' : `/api/v1/agents/${id}`, {
        method: isNew ? 'POST' : 'PATCH',
        body: JSON.stringify(
          toAgentInput({
            ...form,
            ...(form.webSearchEnabled !== undefined
              ? {
                  allowedSearchDomains: searchDomains.split(/\s+/).filter(Boolean),
                }
              : {}),
          }),
        ),
      }),
    onSuccess: (agent) => {
      void client.invalidateQueries({ queryKey: ['agents'] });
      void client.invalidateQueries({ queryKey: ['agent', agent.id] });
      void navigate(`/agents/${agent.id}`, { replace: true });
    },
  });
  const addOrigin = useMutation({
    mutationFn: () =>
      api(`/api/v1/agents/${id}/origins`, { method: 'POST', body: JSON.stringify({ origin }) }),
    onSuccess: () => {
      setOrigin('');
      void client.invalidateQueries({ queryKey: ['agent', id] });
    },
  });
  const removeOrigin = useMutation({
    mutationFn: (originId: string) =>
      api(`/api/v1/agents/${id}/origins/${originId}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['agent', id] }),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const valid = webSearchConfigSchema.safeParse({
      webSearchEnabled: form.webSearchEnabled ?? false,
      allowedSearchDomains: searchDomains.split(/\s+/).filter(Boolean),
    });
    if (!valid.success) {
      setSearchError(
        'Enter up to 20 domains without https://, paths or wildcards. Web Search needs at least one domain when enabled.',
      );
      return;
    }
    setSearchError('');
    save.mutate();
  };
  return (
    <Page
      title={isNew ? 'Create agent' : 'Agent configuration'}
      description="Changes are explicit and tenant-scoped."
      actions={
        <Link className="button" to="/agents">
          Back to agents
        </Link>
      }
    >
      <State loading={!isNew && query.isLoading} error={query.error}>
        <form className="two-col" onSubmit={submit}>
          <Card>
            <h2>Behavior</h2>
            <label>
              Default language
              <select
                value={form.defaultLocale ?? 'en'}
                onChange={(e) =>
                  setForm({ ...form, defaultLocale: e.target.value === 'id' ? 'id' : 'en' })
                }
              >
                <option value="en">English</option>
                <option value="id">Bahasa Indonesia</option>
              </select>
            </label>
            <label>
              Greeting (Indonesian)
              <textarea
                maxLength={500}
                value={form.greetingId ?? ''}
                onChange={(e) => setForm({ ...form, greetingId: e.target.value })}
              />
            </label>
            <label>
              Greeting (English)
              <textarea
                maxLength={500}
                value={form.greetingEn ?? ''}
                onChange={(e) => setForm({ ...form, greetingEn: e.target.value })}
              />
            </label>
            <small>
              Optional translations. Empty translations use the original Greeting below. Default
              language is used when the visitor has no supported preference.
            </small>
            <label>
              Display name
              <input
                required
                maxLength={100}
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              />
            </label>
            <label>
              Greeting
              <textarea
                required
                maxLength={500}
                rows={3}
                value={form.greeting}
                onChange={(e) => setForm({ ...form, greeting: e.target.value })}
              />
            </label>
            <label>
              Instructions
              <textarea
                maxLength={10000}
                rows={10}
                value={form.instructions}
                onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              />
              <small>Documents cannot override platform safety rules.</small>
            </label>
          </Card>
          <Card>
            <fieldset className="web-search-settings">
              <legend>Web Search</legend>
              <label className="check">
                <input
                  type="checkbox"
                  checked={form.webSearchEnabled ?? false}
                  onChange={(event) => setForm({ ...form, webSearchEnabled: event.target.checked })}
                />
                Enable Web Search
              </label>
              <p>Off by default. File Search stays available for uploaded knowledge.</p>
              <label>
                Allowed search domains
                <textarea
                  rows={4}
                  value={searchDomains}
                  maxLength={5080}
                  required={form.webSearchEnabled ?? false}
                  aria-describedby="search-domains-help"
                  aria-invalid={Boolean(searchError)}
                  onChange={(event) => {
                    setSearchDomains(event.target.value);
                    setForm({ ...form, webSearchEnabled: form.webSearchEnabled ?? false });
                  }}
                />
              </label>
              <small id="search-domains-help">
                One domain per line, up to 20. Use example.com, without https:// or a path.
                Subdomains are included. These are search sources, not widget origins. At least one
                domain is required to enable search. Searches add provider costs and may take
                longer; daily limits apply.
              </small>
              {searchError && (
                <p role="alert" className="form-error">
                  {searchError}
                </p>
              )}
            </fieldset>
            <h2>Presentation & availability</h2>
            <label>
              Primary color
              <input
                type="color"
                value={form.primaryColor}
                onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
              />
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Agent is active
            </label>
            <fieldset className="visitor-action-settings">
              <legend>Visitor actions</legend>
              <label className="check">
                <input
                  type="checkbox"
                  checked={form.contactCaptureEnabled ?? true}
                  onChange={(event) =>
                    setForm({ ...form, contactCaptureEnabled: event.target.checked })
                  }
                />
                Show “Share contact details” button
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={form.humanHandoffEnabled ?? true}
                  onChange={(event) =>
                    setForm({ ...form, humanHandoffEnabled: event.target.checked })
                  }
                />
                Show “Request a person” button
              </label>
              <small>
                Choose which follow-up actions visitors can use in the widget and public demo.
              </small>
            </fieldset>
            {!isNew && query.data && (
              <div className="code-block">
                <small>Public agent ID</small>
                <code>{query.data.publicId}</code>
              </div>
            )}
            {save.error && (
              <p className="form-error" role="alert">
                {save.error.message}
              </p>
            )}
            <button className="primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save agent'}
            </button>
          </Card>
        </form>
        {!isNew && query.data && <AgentSharing agent={query.data} />}
        {!isNew && (
          <Card>
            <h2>Allowed website origins</h2>
            <p>Use the exact scheme and host. Paths are discarded.</p>
            <form
              className="inline-form"
              onSubmit={(e) => {
                e.preventDefault();
                addOrigin.mutate();
              }}
            >
              <label className="sr-only" htmlFor="origin">
                Website origin
              </label>
              <input
                id="origin"
                type="url"
                placeholder="https://www.example.com"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                required
              />
              <button disabled={addOrigin.isPending}>Add origin</button>
            </form>
            {addOrigin.error && (
              <p className="form-error" role="alert">
                {addOrigin.error.message}
              </p>
            )}
            <ul className="rows origin-rows">
              {query.data?.allowedOrigins.map((item) => (
                <li key={item.id}>
                  <code>{item.origin}</code>
                  <div>
                    <button
                      className="danger ghost"
                      disabled={removeOrigin.isPending}
                      onClick={() => removeOrigin.mutate(item.id)}
                    >
                      {removeOrigin.isPending && removeOrigin.variables === item.id
                        ? 'Removing...'
                        : 'Remove'}
                    </button>
                    {removeOrigin.isPending && removeOrigin.variables === item.id && (
                      <span className="sr-only" role="status">
                        Removing website origin...
                      </span>
                    )}
                    {removeOrigin.isError && removeOrigin.variables === item.id && (
                      <p className="mutation-error" role="alert">
                        Could not remove this origin. Select Remove to retry.
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </State>
    </Page>
  );
}
